import React, { useMemo, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { useModules } from "newui/features/RolesPermissions/hooks/useRoleMatrix";
import { useUserEffectivePermissions } from "newui/features/RolesPermissions/hooks/useUserEffectivePermissions";
import { useUserAssignments } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type {
	EffectiveCapabilityFlags,
	PermissionLevel,
	ScopeType,
	UserEffectivePermissions,
	UserRoleAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { capitalise } from "utils/helpers";

export interface UserAccessMatrixModalProps {
	isOpen: boolean;
	userId?: number;
	userName?: string;
	onClose: () => void;
	/**
	 * PRO-731 bug A — "self" scopes the modal to the CURRENT user viewing their
	 * OWN access from My Profile.
	 *
	 * `GET /rbac/users/{id}/permissions` itself returns 200 for a user reading
	 * their own record (confirmed against a live non-admin session) — it is
	 * NOT gated against self-view and both modes keep reading it. The 403s
	 * that broke this for a non-admin came from the OTHER two queries this
	 * modal fires alongside it: `/rbac/modules` (`can:viewAny,Role` —
	 * System Admin only) and `/rbac/users` (`can:viewAny,User`, via
	 * `useUserAssignments`, backing the "Project & RFx" tab), whose failures
	 * were folded straight into `isLoading`/`isError` for every caller,
	 * including someone who could see their own permissions payload just fine.
	 *
	 * Self mode still fetches `/rbac/users/{id}/permissions`, but never fires
	 * `/rbac/modules` or `/rbac/users`: the module row list is derived from
	 * the permissions payload itself (it already enumerates every module,
	 * "none" cells included — see `useModuleCan`'s doc comment), and the
	 * "Project & RFx" tab never appears (its data comes from the gated
	 * `/rbac/users` list, which self mode has no substitute for).
	 *
	 * Defaults to "admin" — the existing User Management behaviour is
	 * unchanged unless a caller opts into "self".
	 */
	mode?: "admin" | "self";
}

interface AccessModuleRow {
	id: number | string;
	key: string;
	name: string;
}

// Self mode has no `/rbac/modules` names to draw on (admin-gated), so a
// module's row label is derived from its key instead — e.g. "company_profile"
// -> "Company profile". `permissions.modules` already includes every module
// the server knows about (including "none" cells, per useModuleCan), so this
// is a complete row list, just without the curated admin ordering/casing.
const deriveSelfModules = (permissions: UserEffectivePermissions | undefined): AccessModuleRow[] => {
	if (!permissions) {
		return [];
	}

	return Object.keys(permissions.modules)
		.sort()
		.map((key) => ({ id: key, key, name: capitalise(key) }));
};

interface LevelDisplay {
	fillClassName: string;
	label: string;
	textClassName: string;
}

type AccessTab = "company" | "scoped";

// Mirrors PermissionMatrixEditor's LEVEL_CONFIG so the read-only view reads with
// the same colour language as the editable matrix (server-authoritative levels).
const LEVEL_DISPLAY: Record<PermissionLevel, LevelDisplay> = {
	full: { label: "Full", fillClassName: "bg-emerald-100 border-emerald-300", textClassName: "text-emerald-800" },
	view: { label: "View", fillClassName: "bg-blue-100 border-blue-300", textClassName: "text-blue-800" },
	comment: { label: "Comment", fillClassName: "bg-amber-100 border-amber-300", textClassName: "text-amber-900" },
	event: { label: "Event only", fillClassName: "bg-slate-100 border-slate-300", textClassName: "text-slate-700" },
	none: { label: "No access", fillClassName: "bg-gray-50 border-gray-200", textClassName: "text-gray-400" },
};

// Column order for the capability flags — the seven verbs the backend resolves.
const CAPABILITY_COLUMNS: Array<{ key: keyof EffectiveCapabilityFlags; label: string }> = [
	{ key: "view", label: "View" },
	{ key: "comment", label: "Comment" },
	{ key: "create", label: "Create" },
	{ key: "edit", label: "Edit" },
	{ key: "approve", label: "Approve" },
	{ key: "close", label: "Close" },
	{ key: "delete", label: "Delete" },
];

// Human labels for the non-global scope types shown in the "Project & RFx" tab.
const SCOPE_TYPE_LABEL: Record<Exclude<ScopeType, "global">, string> = {
	project: "Project",
	rfx: "RFx",
	project_type: "Project type",
};

const CapabilityDot = ({ granted }: { granted: boolean }) => {
	if (granted) {
		return <span className="text-emerald-600" aria-label="allowed">✓</span>;
	}

	return <span className="text-gray-300" aria-label="not allowed">—</span>;
};

// PRO-730 — negative ids are tour-fixture dummy users (see
// guides/features/Auth/tourFixtures.ts). The "View a user's access" guided
// tour opens this modal for one so it can point at the tabs/roles without a
// real user, so none of the three real queries below may ever fire for it —
// static demo content renders instead, matching the fixture's single global
// "Team Member" role and one project grant (so the Project & RFx tab has
// something to show).
const isDummyAccessUserId = (id: number | undefined): boolean => typeof id === "number" && id < 0;

const TOUR_DEMO_CAPABILITY_FLAGS: EffectiveCapabilityFlags = {
	view: true,
	comment: true,
	create: false,
	edit: false,
	approve: false,
	close: false,
	delete: false,
};

const TOUR_DEMO_MODULES: AccessModuleRow[] = [
	{ id: "rfx", key: "rfx", name: "RFx Events" },
	{ id: "projects", key: "projects", name: "Projects" },
];

const TOUR_DEMO_PERMISSIONS: UserEffectivePermissions = {
	roles: ["Team Member"],
	modules: {
		rfx: { level: "view", can: TOUR_DEMO_CAPABILITY_FLAGS },
		projects: { level: "view", can: TOUR_DEMO_CAPABILITY_FLAGS },
	},
};

const TOUR_DEMO_SCOPED_ASSIGNMENTS: UserRoleAssignment[] = [
	{
		assignment_id: -1,
		role_id: -1,
		role_name: "Team Member",
		scope_type: "project",
		scope_id: -1,
		scope_label: "Demo Project",
	},
];

/**
 * PRO-413 #UM1 — read-only view of a single user's access.
 *  - "Company-wide" tab (default): EFFECTIVE standing access as a module × capability
 *    matrix, straight from GET /rbac/users/{user}/permissions (H1 standing-only).
 *  - "Project & RFx" tab: the user's resource-scoped role assignments (project /
 *    RFx events), from the assignments record — the access that a company-wide view
 *    can't express. Shown only when the user has such grants.
 * Server-authoritative throughout; the FE never derives access itself.
 */
export const UserAccessMatrixModal = ({
	isOpen,
	userId,
	userName,
	onClose,
	mode = "admin",
}: UserAccessMatrixModalProps) => {
	const isSelf = mode === "self";
	// PRO-730 — see TOUR_DEMO_* above: none of the real queries below may fire
	// for a tour-fixture dummy user id.
	const isDummy = isDummyAccessUserId(userId);

	// `/rbac/users/{id}/permissions` is fetched in BOTH modes — it 200s for a
	// user reading their own record. `/rbac/modules` and `/rbac/users` are
	// System Admin/admin-only respectively and are only fetched (and only
	// allowed to drive isLoading/isError) for the admin view.
	const modulesQuery = useModules(isOpen && !isSelf && !isDummy);
	const permissionsQuery = useUserEffectivePermissions(isOpen && !isDummy ? userId : undefined);

	// Backs the "Project & RFx" tab. Admin-gated, so never fetched in self
	// mode — `assignmentUsers` stays `[]`, so `scopedAssignments` below
	// naturally resolves empty and that tab never renders.
	const { users: assignmentUsers } = useUserAssignments({ enabled: isOpen && !isSelf && !isDummy });

	const [tab, setTab] = useState<AccessTab>("company");

	const isLoading = !isDummy && (isSelf ? permissionsQuery.isLoading : modulesQuery.isLoading || permissionsQuery.isLoading);
	const isError = !isDummy && (isSelf ? permissionsQuery.isError : modulesQuery.isError || permissionsQuery.isError);

	const permissions = isDummy ? TOUR_DEMO_PERMISSIONS : permissionsQuery.data;
	const modules: AccessModuleRow[] = isDummy
		? TOUR_DEMO_MODULES
		: isSelf
			? deriveSelfModules(permissions)
			: (modulesQuery.data ?? []).map((module) => ({ id: module.id, key: module.key, name: module.name }));
	const roles = permissions?.roles ?? [];

	// Resource-scoped (non-global) assignments for this user — the "Project & RFx"
	// tab. Company-wide (global) rows are represented by the standing matrix instead.
	const scopedAssignments = useMemo(() => {
		if (isDummy) {
			return TOUR_DEMO_SCOPED_ASSIGNMENTS;
		}
		const record = assignmentUsers.find((entry) => entry.id === userId);
		return (record?.assignments ?? []).filter((assignment) => assignment.scope_type !== "global");
	}, [isDummy, assignmentUsers, userId]);

	const title = userName ? `Access for ${userName}` : "User access";

	const tabButtonClass = (active: boolean): string =>
		`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
			active
				? "border-blue-600 text-blue-700"
				: "border-transparent text-gray-500 hover:text-gray-700"
		}`;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			description="This user's resolved access — company-wide, plus any project or RFx-specific grants."
			size="xxl"
			// The matrix owns the scrolling so its sticky header/first column stay
			// pinned — `position: sticky` resolves against the nearest scrolling
			// ancestor, so the body must NOT scroll too (PRO-718).
			fillBody
			primaryButton={{ text: "Close", btntype: "secondary", onClick: onClose }}
		>
			<div data-testid="user-access-matrix-modal" className="flex min-h-0 flex-1 flex-col gap-4">
				{roles.length > 0 && (
					<div className="flex flex-wrap items-center gap-2" data-testid="user-access-roles">
						<span className="text-sm font-medium text-gray-600">Roles:</span>
						{roles.map((role) => (
							<span
								key={role}
								className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
							>
								{role}
							</span>
						))}
					</div>
				)}

				{/* Tabs: default to the company-wide matrix; the scoped tab appears only
				    when the user actually has project/RFx grants. */}
				<div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={tab === "company"}
						className={tabButtonClass(tab === "company")}
						onClick={() => setTab("company")}
						data-testid="user-access-tab-company"
					>
						Company-wide
					</button>
					{scopedAssignments.length > 0 && (
						<button
							type="button"
							role="tab"
							aria-selected={tab === "scoped"}
							className={tabButtonClass(tab === "scoped")}
							onClick={() => setTab("scoped")}
							data-testid="user-access-tab-scoped"
						>
							Project &amp; RFx access
							<span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
								{scopedAssignments.length}
							</span>
						</button>
					)}
				</div>

				{isLoading && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
						<Spinner size="sm" /> Loading access…
					</div>
				)}

				{!isLoading && isError && (
					<div className="py-10 text-center text-sm text-red-600" data-testid="user-access-error">
						Could not load this user&apos;s access. Please try again.
					</div>
				)}

				{!isLoading && !isError && tab === "company" && (
					<div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
						<table className="min-w-full border-collapse text-sm">
							<thead className="sticky top-0 z-10">
								<tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
									<th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-semibold">Module</th>
									<th className="bg-gray-50 px-3 py-2 font-semibold">Level</th>
									{CAPABILITY_COLUMNS.map((column) => (
										<th key={column.key} className="bg-gray-50 px-3 py-2 text-center font-semibold">
											{column.label}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{modules.map((module) => {
									const capability = permissions?.modules[module.key];
									const level: PermissionLevel = capability?.level ?? "none";
									const display = LEVEL_DISPLAY[level];

									return (
										<tr
											key={module.id}
											className="border-t border-gray-100"
											data-testid={`user-access-row-${module.key}`}
										>
											<th
												scope="row"
												className="sticky left-0 z-[1] bg-white px-3 py-2 text-left font-medium text-gray-700"
											>
												{module.name}
											</th>
											<td className="px-3 py-2">
												<span
													className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${display.fillClassName} ${display.textClassName}`}
												>
													{display.label}
												</span>
											</td>
											{CAPABILITY_COLUMNS.map((column) => (
												<td key={column.key} className="px-3 py-2 text-center">
													<CapabilityDot granted={Boolean(capability?.can[column.key])} />
												</td>
											))}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{!isLoading && !isError && tab === "scoped" && (
					<div className="min-h-0 flex-1 overflow-auto" data-testid="user-access-scoped-list">
						{scopedAssignments.length === 0 ? (
							<p className="py-10 text-center text-sm text-gray-500" data-testid="user-access-scoped-empty">
								No project or RFx-specific access.
							</p>
						) : (
							<ul className="flex flex-col gap-2">
								{scopedAssignments.map((assignment) => (
									<li
										key={assignment.assignment_id}
										className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5"
										data-testid={`user-access-scoped-row-${assignment.assignment_id}`}
									>
										<div className="flex flex-col">
											<span className="text-sm font-medium text-gray-800">{assignment.role_name}</span>
											<span className="text-xs text-gray-500">
												{assignment.scope_label ?? "—"}
											</span>
										</div>
										<span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
											{assignment.scope_type === "global"
												? "Company-wide"
												: SCOPE_TYPE_LABEL[assignment.scope_type]}
										</span>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
};
